import React, { useEffect, useRef, useState } from 'react'

import { Button, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'

import { ANALYTICS_SURFACES } from '../analytics/compliance-analytics.constants'
import { useComplianceAnalytics } from '../analytics/use-compliance-analytics'
import {
  fetchIntake,
  fetchReportOffers,
  loadReport,
  retryReport,
} from '../compliance.actions'
import { selectLicenses, selectReport } from '../compliance.selectors'
import { ReportBuildingScreen } from '../components/ReportBuildingScreen'
import { ReportView } from '../components/ReportView'
import {
  FETCH_REPORT_KEY,
  licensedStateCodes,
  stateName,
} from '../lib/constants'
import { useRecoverReportOnResume } from './useRecoverReportOnResume'

export const ComplianceReport: React.FC = () => {
  const dispatch = useAppDispatch()
  const report = useReselector(selectReport)
  const licenses = useReselector(selectLicenses)
  const error = useReselector(getFetchError, FETCH_REPORT_KEY)
  const isFetchingReport = useReselector(getIsFetching, FETCH_REPORT_KEY)
  const [animationDone, setAnimationDone] = useState(false)
  const buildStartedAtRef = useRef(Date.now())
  const renderedRef = useRef(false)
  const { trackRenderedReport } = useComplianceAnalytics(
    ANALYTICS_SURFACES.SignupFunnel
  )

  useRecoverReportOnResume()

  useEffect(() => {
    dispatch(loadReport())
    dispatch(fetchIntake())
    dispatch(fetchReportOffers())
  }, [dispatch])

  // The mount seed above has to be a real clock reading: a remount over a cached report
  // starts no generation, so a zero would report the whole unix epoch as the wait.
  useEffect(() => {
    if (isFetchingReport && !renderedRef.current) {
      buildStartedAtRef.current = Date.now()
    }
  }, [isFetchingReport])

  const reportOnScreen = Boolean(report) && animationDone && !isFetchingReport

  // Measured to the first render of the report itself, building screen included:
  // the animation holds its last step until the request resolves, so it is part
  // of the wait rather than an overlay on top of it.
  useEffect(() => {
    if (!reportOnScreen || !report || renderedRef.current) return
    renderedRef.current = true
    trackRenderedReport({
      build_duration_ms: Date.now() - buildStartedAtRef.current,
      obligation_count: report.areas.reduce(
        (total, area) => total + area.obligations.length,
        0
      ),
    })
  }, [reportOnScreen, report, trackRenderedReport])

  const stateCodes = licensedStateCodes(
    licenses,
    report?.context.jurisdictions ?? []
  )
  const jurisdictions = stateCodes.map(stateName)

  if (error && !report) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-6 py-24 text-center">
        <Text variant={TypographyStyles.DISPLAY_SM} color={TextColors.HEADER}>
          We couldn&apos;t build your report
        </Text>
        <Text variant={TypographyStyles.BODY_MD} color={TextColors.MUTED}>
          Something went wrong on our end. Try again in a moment. Your answers
          are saved.
        </Text>
        <Button
          variant="primary"
          onClick={() => {
            setAnimationDone(false)
            dispatch(retryReport())
          }}
        >
          Try again
        </Button>
      </div>
    )
  }

  // A refetch re-runs the narrative and can word it differently, so it gets
  // the building screen rather than rewriting the page under a reader.
  if (!report || !reportOnScreen) {
    return (
      <ReportBuildingScreen
        jurisdictions={jurisdictions}
        ready={Boolean(report) && !isFetchingReport}
        onFinished={() => setAnimationDone(true)}
      />
    )
  }

  return <ReportView report={report} />
}
